import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import ReplyIcon from '@material-ui/icons/Reply';
import ReplyAllIcon from '@material-ui/icons/ReplyAll';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import AutoAwesomeIcon from '@material-ui/icons/Flare';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DescriptionIcon from '@material-ui/icons/Description';
import { useTranslation } from 'react-i18next';
import { useMailOrbitEnabled } from 'src/utils/mailorbitFeature';
import {
  getAttachmentPolicy,
  getEmailTemplates,
  getMailOrbitErrorKey,
  getMessagingCapabilities,
  getReplySuggestion,
  isMailOrbitError,
  renderTemplate,
  sendReply,
  summarizeConversation,
  translateMessage
} from 'src/utils/MailOrbitService';
import { formatDateTime, formatSize } from '../WorkOrderEmail/format';
import RecipientField from './RecipientField';
import useReplyActivity from './useReplyActivity';
import {
  checkAttachment,
  defaultRecipients,
  isSafeWebLink,
  isValidEmail,
  newIdempotencyKey,
  payloadFingerprint,
  readFileAsBase64,
  replySubject
} from './helpers';

const useStyles = makeStyles(theme => ({
  root: {
    fontFamily: theme.typography.fontFamily,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '70vh',
    // Çerçeve ve dolgu yanıt çubuğundadır (ConversationView replyBar); burada çift çerçeve yok.
    backgroundColor: theme.palette.background.paper
  },
  // Açık durumda gövde burada kaydırılır; düğme çubuğu her zaman görünür kalır.
  scroll: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', paddingTop: 2 },
  recipients: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginBottom: theme.spacing(1) },
  recipient: { flex: '1 1 240px', minWidth: 0 },
  field: { marginTop: theme.spacing(1) },
  bar: { display: 'flex', gap: theme.spacing(1), alignItems: 'center', flexWrap: 'wrap', marginTop: theme.spacing(1), '&:first-child': { marginTop: 0 } },
  spacer: { flex: 1 },
  aiPanel: { marginTop: theme.spacing(1), whiteSpace: 'pre-wrap' },
  attachments: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  attachment: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    color: theme.palette.text.secondary
  }
}));

const TRANSLATE_LANGUAGES = ['tr', 'en', 'de', 'fr', 'es', 'it', 'nl', 'pl'];

// Bu hatalardan sonra aynı gönderim tekrarlanmaz; yeni Idempotency-Key gerekir.
// (conflict/network/unknown: sonuç belirsiz olabilir -> aynı anahtarla tekrar denenir.)
const DEFINITIVE_KINDS = ['validation', 'noAccess', 'unauthorized', 'conversationClosed'];

const usableTarget = (detail, selectedMessage) => {
  const ok = m => m && !m.isMasked && !m.isDeleted;
  if (ok(selectedMessage)) return selectedMessage;
  const list = (detail.messages || []).filter(ok);
  const inbound = list.filter(m => m.direction !== 'Outbound');
  return (inbound.length ? inbound : list).slice(-1)[0] || null;
};

const ReplyComposerInner = ({ detail, selectedMessage, onSent }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation(['mailorbitReply', 'mailorbit']);
  const conversationId = detail.conversationId;

  const [mode, setMode] = useState(null); // null | 'reply' | 'replyAll'
  const [target, setTarget] = useState(null);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // { name, size, contentBase64 }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null); // { text, details, code?, webLink? }
  const [sentInfo, setSentInfo] = useState(null);
  const [caps, setCaps] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [aiBusy, setAiBusy] = useState(null); // 'suggest' | 'summary' | 'translate' | 'template'
  const [aiPanel, setAiPanel] = useState(null); // { kind, text, truncated }
  const [lang, setLang] = useState('');
  const [aiAnchor, setAiAnchor] = useState(null);
  const [aiStep, setAiStep] = useState('main'); // 'main' | 'lang'
  const [tplAnchor, setTplAnchor] = useState(null);
  const keyRef = useRef(null); // { key, fp }
  const sendingRef = useRef(false);
  const loadedRef = useRef(false);

  const drafting = mode !== null;
  const { activity, refresh } = useReplyActivity({ conversationId, enabled: true, drafting });

  // Konuşma değişirse form sıfırlanır.
  useEffect(() => {
    setMode(null);
    setSentInfo(null);
    setError(null);
    setAiPanel(null);
    keyRef.current = null;
  }, [conversationId]);

  useEffect(() => {
    if (!lang) {
      const cur = ((i18n && i18n.language) || 'en').slice(0, 2);
      setLang(TRANSLATE_LANGUAGES.includes(cur) ? cur : 'en');
    }
  }, [i18n, lang]);

  const loadOptions = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const safe = p => p.catch(() => null);
    const [c, p, tpl] = await Promise.all([
      safe(getMessagingCapabilities()),
      safe(getAttachmentPolicy()),
      safe(getEmailTemplates())
    ]);
    setCaps(c);
    setPolicy(p);
    setTemplates(Array.isArray(tpl) ? tpl : []);
  }, []);

  const open = nextMode => {
    const msg = usableTarget(detail, selectedMessage);
    if (!msg) return;
    const rec = defaultRecipients(msg, nextMode);
    setTarget(msg);
    setMode(nextMode);
    setTo(rec.to);
    setCc(rec.cc);
    setSubject(replySubject(msg.subject || detail.subject));
    setBody('');
    setAttachments([]);
    setError(null);
    setSentInfo(null);
    setAiPanel(null);
    keyRef.current = null;
    loadOptions();
    refresh();
  };

  // Taslak açıkken Cevapla / Tümünü cevapla arasında geçiş: yazılan metin ve ekler korunur.
  const switchMode = nextMode => {
    if (nextMode === mode || !target) return;
    const rec = defaultRecipients(target, nextMode);
    setMode(nextMode);
    setTo(rec.to);
    setCc(rec.cc);
  };

  const close = () => {
    setMode(null);
    setError(null);
  };

  const messageId = target ? target.conversationMessageId : null;

  const reportError = e => {
    if (isMailOrbitError(e)) {
      // Cevapta 409 = gönderim sürüyor (send_in_progress); genel "değişti" metni yanıltıcı olur.
      const key = e.kind === 'conflict' ? null : getMailOrbitErrorKey(e);
      const problemErrors = e.problem && e.problem.errors;
      const details = problemErrors
        ? [].concat(...Object.keys(problemErrors).map(k => problemErrors[k])).filter(x => typeof x === 'string')
        : [];
      setError({
        text: key ? t(`mailorbit:${key}`) : t('mailorbitReply:send_in_progress'),
        details
      });
    } else {
      setError({ text: t('mailorbit:error_unknown'), details: [] });
    }
  };

  const runAi = async (kind, fn, apply) => {
    if (aiBusy) return;
    setAiBusy(kind);
    setError(null);
    try {
      const res = await fn();
      if (res && res.success) {
        apply(res);
      } else {
        setError({
          text: t(`mailorbitReply:reason_${(res && res.reasonCode) || 'unknown'}`, {
            defaultValue: t('mailorbitReply:ai_unavailable')
          }),
          details: []
        });
      }
    } catch (e) {
      reportError(e);
    } finally {
      setAiBusy(null);
    }
  };

  const appendText = text =>
    setBody(prev => (prev && prev.trim() ? `${prev.replace(/\s+$/, '')}\n\n${text}` : text));

  const onSuggest = () =>
    runAi('suggest', () => getReplySuggestion(conversationId, messageId), res =>
      appendText(res.suggestedText || '')
    );

  const onSummary = () =>
    runAi('summary', () => summarizeConversation(conversationId), res =>
      setAiPanel({ kind: 'summary', text: res.text || '', truncated: !!res.truncated })
    );

  const onTranslate = targetLang => {
    setLang(targetLang);
    return runAi('translate', () => translateMessage(conversationId, messageId, targetLang), res =>
      setAiPanel({ kind: 'translate', text: res.text || '', truncated: !!res.truncated })
    );
  };

  const onTemplate = id => {
    setTplAnchor(null);
    if (!id) return;
    runAi('template', () => renderTemplate(conversationId, messageId, id), res =>
      appendText(res.text || '')
    );
  };

  const onFiles = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setError(null);
    let list = attachments;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const problem = checkAttachment(file, list, policy);
      if (problem) {
        setError({ text: `${file.name}: ${t(`mailorbitReply:${problem}`)}`, details: [] });
        break;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const contentBase64 = await readFileAsBase64(file);
        list = [...list, { name: file.name, size: file.size, contentBase64 }];
      } catch (err) {
        setError({ text: `${file.name}: ${t('mailorbitReply:attach_read_failed')}`, details: [] });
        break;
      }
    }
    setAttachments(list);
  };

  const send = async () => {
    if (sendingRef.current) return; // çift tıklama: ikinci istek atılmaz (aynı gönderim)
    const invalid = [...to, ...cc].filter(a => !isValidEmail(a));
    if (!to.length) {
      setError({ text: t('mailorbitReply:no_recipients'), details: [] });
      return;
    }
    if (invalid.length) {
      setError({ text: t('mailorbitReply:invalid_recipients', { list: invalid.join(', ') }), details: [] });
      return;
    }
    if (!body.trim()) {
      setError({ text: t('mailorbitReply:empty_body'), details: [] });
      return;
    }

    const payload = {
      toRecipients: to,
      ccRecipients: cc,
      subject,
      bodyText: body,
      attachments: attachments.map(a => ({ fileName: a.name, contentBase64: a.contentBase64 }))
    };
    // Aynı içerikle yeniden deneme (ağ hatası / belirsiz sonuç) aynı anahtarı kullanır;
    // içerik değişmişse ya da önceki deneme kesin bittiyse yeni anahtar üretilir.
    const fp = payloadFingerprint(messageId, payload);
    if (!keyRef.current || keyRef.current.fp !== fp) keyRef.current = { key: newIdempotencyKey(), fp };

    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      const res = await sendReply(conversationId, messageId, {
        idempotencyKey: keyRef.current.key,
        ...payload
      });
      if (res && res.success) {
        keyRef.current = null;
        setSentInfo({ webLink: isSafeWebLink(res.webLink) ? res.webLink : null });
        setMode(null);
        if (onSent) onSent();
      } else {
        const code = (res && res.reasonCode) || 'unknown';
        if (code !== 'send_in_progress') keyRef.current = null;
        setError({
          text: t(`mailorbitReply:reason_${code}`, { defaultValue: t('mailorbitReply:send_failed') }),
          code,
          details: [],
          webLink: res && isSafeWebLink(res.webLink) ? res.webLink : null
        });
      }
    } catch (e) {
      if (isMailOrbitError(e) && DEFINITIVE_KINDS.includes(e.kind)) keyRef.current = null;
      reportError(e);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const closed = detail.statusIsTerminal === true;
  const others = (activity && activity.otherDrafters) || [];
  const recent = activity && activity.recentReplyByOther;

  const notice = (
    <>
      {others.length > 0 && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {t('mailorbitReply:others_drafting', {
            names: others.map(d => d.displayName || t('mailorbitReply:another_agent')).join(', ')
          })}
        </Alert>
      )}
      {recent && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {recent.inProgress
            ? t('mailorbitReply:other_sending', { name: recent.displayName || t('mailorbitReply:another_agent') })
            : t('mailorbitReply:other_replied', {
                name: recent.displayName || t('mailorbitReply:another_agent'),
                when: formatDateTime(recent.atUtc)
              })}
        </Alert>
      )}
    </>
  );

  if (closed) {
    return (
      <Box className={classes.root}>
        <Alert severity="info">{t('mailorbitReply:closed_reopen_first')}</Alert>
      </Box>
    );
  }

  const aiEnabled = !caps || caps.replyWithAi !== false;
  const hasTarget = !!usableTarget(detail, selectedMessage);
  const busy = sending || !!aiBusy;

  const closeAi = () => setAiAnchor(null);
  const runFromMenu = fn => {
    closeAi();
    fn();
  };

  const mainItems = [
    <MenuItem key="suggest" onClick={() => runFromMenu(onSuggest)}>
      {t('mailorbitReply:ai_suggest')}
    </MenuItem>,
    <MenuItem key="summary" onClick={() => runFromMenu(onSummary)}>
      {t('mailorbitReply:summarize')}
    </MenuItem>,
    <MenuItem key="translate" onClick={() => setAiStep('lang')}>
      <ListItemText primary={t('mailorbitReply:translate')} />
      <ListItemIcon style={{ minWidth: 0, marginLeft: 16 }}>
        <ChevronRightIcon fontSize="small" />
      </ListItemIcon>
    </MenuItem>
  ];
  const langItems = [
    <MenuItem key="back" onClick={() => setAiStep('main')} aria-label={t('mailorbitReply:translate_back')}>
      <ListItemIcon style={{ minWidth: 32 }}>
        <ArrowBackIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={t('mailorbitReply:translate_to')} />
    </MenuItem>,
    ...TRANSLATE_LANGUAGES.map(l => (
      <MenuItem key={l} selected={l === lang} onClick={() => runFromMenu(() => onTranslate(l))}>
        {t(`mailorbitReply:lang_${l}`)}
      </MenuItem>
    ))
  ];
  const aiMenuItems = aiStep === 'main' ? mainItems : langItems;

  return (
    <Box className={classes.root}>
      <div className={drafting ? classes.scroll : undefined}>
        {sentInfo && (
          <Alert severity="success" style={{ marginBottom: 8 }} onClose={() => setSentInfo(null)}>
            {t('mailorbitReply:sent')}{' '}
            {sentInfo.webLink && (
              <Link href={sentInfo.webLink} target="_blank" rel="noopener noreferrer">
                {t('mailorbitReply:open_in_outlook')}
              </Link>
            )}
          </Alert>
        )}
        {notice}
        {drafting && (
          <>
            <Box className={classes.recipients} style={{ marginTop: 8 }}>
              <Box className={classes.recipient}>
                <RecipientField label={t('mailorbitReply:to')} value={to} onChange={setTo} disabled={sending} />
              </Box>
              <Box className={classes.recipient}>
                <RecipientField label={t('mailorbitReply:cc')} value={cc} onChange={setCc} disabled={sending} />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t('mailorbitReply:subject')}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              disabled={sending}
            />
            {aiPanel && (
              <Alert severity="info" className={classes.aiPanel} onClose={() => setAiPanel(null)}>
                <strong>
                  {aiPanel.kind === 'summary'
                    ? t('mailorbitReply:summary_title')
                    : t('mailorbitReply:translation_title')}
                </strong>
                <div>{aiPanel.text}</div>
                {aiPanel.truncated && <em>{t('mailorbitReply:truncated')}</em>}
                <div>
                  <em>{t('mailorbitReply:agent_only_hint')}</em>
                </div>
              </Alert>
            )}
            <TextField
              className={classes.field}
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              variant="outlined"
              label={t('mailorbitReply:body')}
              helperText={t('mailorbitReply:signature_hint')}
              value={body}
              onChange={e => setBody(e.target.value)}
              disabled={sending}
            />
            {attachments.length > 0 && (
              <Box className={classes.attachments}>
                {attachments.map((a, i) => (
                  <Box key={`${a.name}-${i}`} className={classes.attachment}>
                    <AttachFileIcon fontSize="small" color="disabled" />
                    <span>
                      {a.name} ({formatSize(a.size)})
                    </span>
                    <IconButton
                      size="small"
                      disabled={sending}
                      onClick={() => setAttachments(list => list.filter((x, j) => j !== i))}
                      aria-label={t('mailorbitReply:remove_attachment')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            {error && (
              <Alert severity="error" style={{ marginTop: 8 }}>
                {error.text}
                {error.code ? ` (${error.code})` : ''}
                {(error.details || []).map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
                {error.webLink && (
                  <div>
                    <Link href={error.webLink} target="_blank" rel="noopener noreferrer">
                      {t('mailorbitReply:open_in_outlook')}
                    </Link>
                  </div>
                )}
              </Alert>
            )}
          </>
        )}
      </div>
      {drafting && (
        <Box className={classes.bar}>
          <Button variant="contained" color="primary" onClick={send} disabled={sending}>
            {sending ? <CircularProgress size={18} /> : t('mailorbitReply:send')}
          </Button>
          <Button onClick={close} disabled={sending}>
            {t('mailorbitReply:cancel')}
          </Button>
          <span className={classes.spacer} />
          <em style={{ fontSize: 12 }}>{t('mailorbitReply:plain_text_hint')}</em>
        </Box>
      )}
      <Box className={classes.bar}>
        <Button
          variant={mode === 'reply' || !drafting ? 'contained' : 'outlined'}
          color="primary"
          startIcon={<ReplyIcon />}
          onClick={() => (drafting ? switchMode('reply') : open('reply'))}
          disabled={!hasTarget || sending}
        >
          {t('mailorbitReply:reply')}
        </Button>
        <Button
          variant={mode === 'replyAll' ? 'contained' : 'outlined'}
          color={mode === 'replyAll' ? 'primary' : 'default'}
          startIcon={<ReplyAllIcon />}
          onClick={() => (drafting ? switchMode('replyAll') : open('replyAll'))}
          disabled={!hasTarget || sending}
        >
          {t('mailorbitReply:reply_all')}
        </Button>
        <span className={classes.spacer} />
        {aiBusy && <CircularProgress size={18} />}
        {aiEnabled && (
          <Tooltip title={t('mailorbitReply:ai_menu_hint')}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                endIcon={<ArrowDropDownIcon />}
                aria-haspopup="true"
                disabled={!drafting || busy}
                onClick={e => {
                  setAiStep('main');
                  setAiAnchor(e.currentTarget);
                }}
              >
                {t('mailorbitReply:ai_menu')}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<DescriptionIcon />}
          endIcon={<ArrowDropDownIcon />}
          aria-haspopup="true"
          title={t('mailorbitReply:template')}
          disabled={!drafting || busy || !templates.length}
          onClick={e => setTplAnchor(e.currentTarget)}
        >
          {t('mailorbitReply:template_short')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          component="label"
          startIcon={<AttachFileIcon />}
          title={t('mailorbitReply:add_attachment')}
          disabled={!drafting || sending}
        >
          {t('mailorbitReply:attach')}
          <input type="file" multiple hidden onChange={onFiles} />
        </Button>
      </Box>
      <Menu anchorEl={aiAnchor} open={!!aiAnchor} onClose={closeAi}>
        {aiMenuItems}
      </Menu>
      <Menu anchorEl={tplAnchor} open={!!tplAnchor} onClose={() => setTplAnchor(null)}>
        {templates.map(tpl => (
          <MenuItem key={tpl.id} title={tpl.description || ''} onClick={() => onTemplate(tpl.id)}>
            {tpl.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

ReplyComposerInner.propTypes = {
  detail: PropTypes.object.isRequired,
  selectedMessage: PropTypes.object,
  onSent: PropTypes.func
};

/**
 * MailOrbit cevap kutusu (SR-010). Kapı (GET MailOrbitFeature) kapalıyken hiçbir şey çizmez ve
 * hiçbir istek atmaz. Mail imzası/layout'u MailOrbit uygular; istemci imza eklemez.
 */
const ReplyComposer = props => {
  const enabled = useMailOrbitEnabled();
  const { detail } = props;
  if (!enabled || !detail || detail.canWork === false) return null;
  return <ReplyComposerInner {...props} />;
};

ReplyComposer.propTypes = {
  detail: PropTypes.object,
  selectedMessage: PropTypes.object,
  onSent: PropTypes.func
};

export default ReplyComposer;
